/**
 * Hooks TanStack Query para el reporte de consumo de IA (Doc 25 §F3).
 *
 * Centraliza summary, breakdown, records y balance. Cache 30s — los
 * datos cambian al ritmo de las invocaciones LLM, refrescar más
 * seguido no agrega valor.
 */

import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import {
  getUsageBalance,
  getUsageBreakdown,
  getUsageRecords,
  getUsageSummary,
  type UsageBalance,
  type UsageBreakdown,
  type UsageFilters,
  type UsageGroupBy,
  type UsageRecordsFilters,
  type UsageRecordsPage,
  type UsageSummary,
} from '../api/usage'
import { useAccessToken } from '../auth/useAccessToken'

const STALE = 30_000

export function useUsageSummary(
  filters: UsageFilters = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<UsageSummary, Error> {
  const { accessToken } = useAccessToken()
  const enabled = (options.enabled ?? true) && !!accessToken
  return useQuery({
    queryKey: ['usage-summary', filters],
    queryFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return getUsageSummary(accessToken, filters)
    },
    enabled,
    staleTime: STALE,
  })
}

export function useUsageBreakdown(
  groupBy: UsageGroupBy,
  filters: UsageFilters = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<UsageBreakdown, Error> {
  const { accessToken } = useAccessToken()
  const enabled = (options.enabled ?? true) && !!accessToken
  return useQuery({
    queryKey: ['usage-breakdown', groupBy, filters],
    queryFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return getUsageBreakdown(accessToken, groupBy, filters)
    },
    enabled,
    staleTime: STALE,
  })
}

/**
 * Paginado infinito sobre el ledger granular. Usa el cursor keyset del
 * backend — `next_cursor` viene en cada página y se pasa a la siguiente
 * petición.
 */
export function useUsageRecords(
  filters: Omit<UsageRecordsFilters, 'cursor'> = {},
  options: { enabled?: boolean } = {},
): UseInfiniteQueryResult<
  { pages: UsageRecordsPage[]; pageParams: (string | undefined)[] },
  Error
> {
  const { accessToken } = useAccessToken()
  const enabled = (options.enabled ?? true) && !!accessToken
  return useInfiniteQuery({
    queryKey: ['usage-records', filters],
    queryFn: ({ pageParam }) => {
      if (!accessToken) throw new Error('not_ready')
      return getUsageRecords(accessToken, {
        ...filters,
        ...(pageParam ? { cursor: pageParam } : {}),
      })
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: UsageRecordsPage) =>
      last.next_cursor ?? undefined,
    enabled,
    staleTime: STALE,
  })
}

export function useUsageBalance(
  options: { enabled?: boolean } = {},
): UseQueryResult<UsageBalance, Error> {
  const { accessToken } = useAccessToken()
  const enabled = (options.enabled ?? true) && !!accessToken
  return useQuery({
    queryKey: ['usage-balance'],
    queryFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return getUsageBalance(accessToken)
    },
    enabled,
    staleTime: STALE,
  })
}
